import React, { useContext, useEffect, useState } from "react";

import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, lighten, useTheme } from "@mui/material/styles";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  listBulletinShare,
  postBulletinShareAdmit,
  postBulletinShareReject,
  BulletinShareListResponse,
} from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTranslation } from "react-i18next";
import {
  Panel,
  Group as PanelGroup,
  Separator as PanelResizeHandle,
} from "react-resizable-panels";
import { useNavigate } from "react-router-dom";

import { AuthStateContext } from "@/contexts/AuthContext";
import { PENDING_BULLETIN_SHARE_COUNT_KEY } from "@/hooks/usePendingBulletinShareCount";

import { BulletinShare } from "./BulletinShare";

dayjs.extend(relativeTime);

type BulletinShareListItem =
  BulletinShareListResponse["bulletin_shares"][number];

const BulletinShareList: React.FC = () => {
  const { currentUserGroups, checkPermission } = useContext(AuthStateContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["bulletin/share"]);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [shares, setShares] = useState<
    BulletinShareListResponse["bulletin_shares"]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedShareId, setSelectedShareId] = useState<number | null>(null);
  const [rejectShareId, setRejectShareId] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    listBulletinShare({ query: { pending: true } })
      .then((res) => {
        setShares(res.data?.bulletin_shares || []);
        setError(null);
      })
      .catch((err) => {
        setError(err?.message || "Failed to load bulletin shares");
      })
      .finally(() => setLoading(false));
  }, []);

  // Only redirect after groups have loaded — currentUserGroups starts as []
  // while async fetch is in progress, so checking before load causes false negatives.
  if (
    currentUserGroups.length > 0 &&
    !checkPermission("manage", "bulletin_sharing")
  ) {
    navigate("/rubialert/alerts");
    return null;
  }

  const removeShare = (id: number) => {
    setShares((prev) => prev.filter((s) => s.bulletin_share.id !== id));
    queryClient.invalidateQueries(PENDING_BULLETIN_SHARE_COUNT_KEY);
    if (selectedShareId === id) setSelectedShareId(null);
  };

  const handleAdmit = (id: number) => {
    postBulletinShareAdmit({ path: { bulletin_share_id: id } }).then(() =>
      removeShare(id),
    );
  };

  const handleReject = (id: number) => {
    postBulletinShareReject({ path: { bulletin_share_id: id } }).then(() =>
      removeShare(id),
    );
  };

  const confirmReject = () => {
    if (rejectShareId === null) return;

    handleReject(rejectShareId);
    setRejectShareId(null);
  };

  if (loading)
    return <CircularProgress sx={{ display: "block", mx: "auto", my: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const columns: GridColDef<BulletinShareListItem>[] = [
    {
      field: "status",
      headerName: t("status", "Status"),
      width: 120,
      renderCell: () => (
        <Chip
          label={t("in_review", "In Review")}
          size="small"
          sx={{
            bgcolor: "warning.main",
            color: colors.common.white,
            fontWeight: 600,
            borderRadius: 10,
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: t("actions", "Actions"),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{ display: "flex", gap: 1.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title={t("admit", "Admit")}>
            <IconButton
              color="primary"
              size="small"
              aria-label={t("admit", "Admit")}
              onClick={() => handleAdmit(row.bulletin_share.id)}
            >
              <CheckCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("reject", "Reject")}>
            <IconButton
              color="error"
              size="small"
              aria-label={t("reject", "Reject")}
              onClick={() => setRejectShareId(row.bulletin_share.id)}
            >
              <CancelOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "bulletin_id",
      headerName: t("bulletin_id", "ID"),
      width: 110,
      valueGetter: (_value, row) => row.bulletin_share.bulletin_id,
    },
    {
      field: "requested_time",
      headerName: t("requested_time", "Requested Time"),
      type: "dateTime",
      width: 160,
      valueGetter: (_value, row) =>
        row.bulletin_share.time_created
          ? new Date(row.bulletin_share.time_created)
          : null,
      renderCell: ({ row }) => {
        const timeCreated = row.bulletin_share.time_created;

        if (!timeCreated) return "-";

        return (
          <Tooltip title={new Date(timeCreated).toLocaleString()}>
            <span>
              {dayjs(timeCreated)
                .locale(i18n.language || "en")
                .fromNow()}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "user",
      headerName: t("requesting_user", "Requesting User"),
      flex: 1,
      minWidth: 220,
      valueGetter: (_value, row) => {
        const user = row.bulletin_share.source_user?.rubi_user;
        const agency = row.bulletin_share.source_org?.title;
        const name = user ? `${user.first_name} ${user.last_name}` : "-";

        return agency ? `${name} (${agency})` : name;
      },
    },
    {
      field: "intended_group",
      headerName: t("destination_group", "Destination Group"),
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row) =>
        row.bulletin_share.destination_group?.name ?? "-",
    },
    {
      field: "agency",
      headerName: t("destination_agency", "Destination Agency"),
      flex: 1,
      minWidth: 160,
      valueGetter: (_value, row) =>
        row.bulletin_share.destination_group?.rubi_org?.title ?? "-",
    },
    {
      field: "expiry",
      headerName: t("expiry", "Expiry"),
      flex: 1,
      minWidth: 190,
      valueGetter: (_value, row) =>
        row.bulletin_share.time_expires
          ? new Date(row.bulletin_share.time_expires).toLocaleString()
          : "-",
    },
  ];

  const table = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: 600, letterSpacing: 0, flexShrink: 0 }}
      >
        {t("bulletinShareList")}
      </Typography>
      <DataGrid
        rows={shares}
        columns={columns}
        getRowId={(row) => row.bulletin_share.id}
        rowSelection={false}
        localeText={{ noRowsLabel: t("No shares available.") }}
        sx={{
          flex: 1,
          "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
            {
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: "inherit",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            },
        }}
        onRowClick={({ row }) => {
          const id = row.bulletin_share.id;
          setSelectedShareId(selectedShareId === id ? null : id);
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{ height: "calc(100vh - 48px)", width: "100%", overflow: "hidden" }}
    >
      <PanelGroup orientation="horizontal" style={{ height: "100%" }}>
        <Panel minSize={30} defaultSize={selectedShareId ? 45 : 100}>
          <Box
            sx={{
              height: "100%",
              px: 4,
              pt: 4,
              pr: selectedShareId ? 1 : 4,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {table}
          </Box>
        </Panel>

        {selectedShareId && (
          <>
            <PanelResizeHandle
              style={{
                width: 6,
                background: theme.palette.grey[300],
                cursor: "col-resize",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 2,
                  height: 40,
                  bgcolor: "grey.400",
                  borderRadius: 1,
                }}
              />
            </PanelResizeHandle>

            <Panel minSize={25} defaultSize={55}>
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderLeft: "1px solid",
                  borderColor: "divider",
                }}
              >
                {/* Sticky toolbar */}
                {(() => {
                  const selectedShare = shares.find(
                    (s) => s.bulletin_share.id === selectedShareId,
                  );
                  const bs = selectedShare?.bulletin_share;
                  const user = bs?.source_user?.rubi_user;
                  const userName = user
                    ? `${user.first_name} ${user.last_name}`
                    : "-";
                  const destGroup = bs?.destination_group?.name ?? "-";
                  return (
                    <Box
                      sx={{
                        flexShrink: 0,
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {/* Top row: actions */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 1.5,
                          px: 3,
                          py: 1.5,
                          bgcolor: alpha(theme.palette.warning.light, 0.4),
                          boxShadow: 6,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            textTransform: "uppercase",
                            borderColor: alpha(
                              theme.palette.common.black,
                              0.23,
                            ),
                            color: "text.primary",
                            "&:hover": { borderColor: "text.primary" },
                          }}
                          startIcon={<CancelOutlinedIcon />}
                          onClick={() => setRejectShareId(selectedShareId)}
                        >
                          {t("reject", "Reject")}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            textTransform: "uppercase",
                            bgcolor: "warning.main",
                            boxShadow: 2,
                            "&:hover": { bgcolor: "warning.dark" },
                          }}
                          startIcon={<CheckCircleOutlineIcon />}
                          onClick={() => handleAdmit(selectedShareId!)}
                        >
                          {t("admit", "Approve")}
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/rubialert/bulletin-share/${selectedShareId}`,
                            )
                          }
                        >
                          <OpenInFullIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setSelectedShareId(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {/* Second row: user → group */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 2,
                          py: 0.75,
                          bgcolor: lighten(theme.palette.warning.light, 0.8),
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: 13, letterSpacing: 0.16 }}
                        >
                          {userName}
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            fontSize: 12,
                            color: "text.primary",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          →
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: 13, letterSpacing: 0.16 }}
                        >
                          {destGroup}
                        </Typography>
                      </Box>
                      <Divider />
                    </Box>
                  );
                })()}
                {/* Scrollable content */}
                <Box sx={{ flex: 1, overflow: "auto", pl: 1 }}>
                  <BulletinShare
                    shareId={String(selectedShareId)}
                    onAdmit={handleAdmit}
                    onReject={handleReject}
                  />
                </Box>
              </Box>
            </Panel>
          </>
        )}
      </PanelGroup>
      <Dialog
        open={rejectShareId !== null}
        onClose={() => setRejectShareId(null)}
        aria-labelledby="reject-bulletin-share-title"
        aria-describedby="reject-bulletin-share-description"
      >
        <DialogTitle id="reject-bulletin-share-title">
          {t("rejectConfirmTitle", "Reject pending bulletin?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reject-bulletin-share-description">
            {t(
              "rejectConfirmDescription",
              "This will remove the bulletin from the pending list and it will not be admitted to the destination group.",
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectShareId(null)}>
            {t("cancel", "Cancel")}
          </Button>
          <Button color="primary" onClick={confirmReject} autoFocus>
            {t("reject", "Reject")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulletinShareList;
